import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { Users, ClipboardText, Notebook, TrendUp, Cake, CurrencyDollar, CheckCircle, WarningCircle, UserMinus, UserCheck } from '@phosphor-icons/react';

function Dashboard() {
  const [stats, setStats] = useState({
    total_students: 0,
    today_attendance: 0,
    total_batches: 0,
    attendance_rate: 0,
    recent_attendance: [],
  });
  const [birthdays, setBirthdays] = useState({
    today_birthdays: [],
    month_birthdays: [],
    current_month: '',
  });
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRole = localStorage.getItem('role') || 'admin';
  const showRevenue = userRole === 'admin';

  useEffect(() => {
    fetchStats();
    fetchBirthdays();
    if (showRevenue) {
      fetchRevenue();
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/attendance/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBirthdays = async () => {
    try {
      const response = await api.get('/students/birthdays');
      setBirthdays(response.data);
    } catch (error) {
      console.error('Error fetching birthdays:', error);
    }
  };

  const fetchRevenue = async () => {
    try {
      const response = await api.get('/dashboard/revenue');
      setRevenue(response.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
    }
  };

  const StatCard = ({ icon: Icon, label, value, color, testId }) => (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 hover:-translate-y-1 hover:border-slate-700 hover:shadow-lg hover:shadow-black/50 transition-all duration-200" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{label}</p>
          <p className="text-3xl font-black tracking-tight text-slate-50">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon size={24} weight="duotone" className="text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="text-slate-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 sm:p-8 lg:p-12">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-slate-50 mb-2">
            Dashboard
          </h1>
          <p className="text-base text-slate-400">Overview of your dance class attendance</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6 mb-8">
          <StatCard
            icon={Users}
            label="Total Students"
            value={stats.total_students}
            color="bg-blue-600"
            testId="stat-total-students"
          />
          <StatCard
            icon={ClipboardText}
            label="Today's Attendance"
            value={stats.today_attendance}
            color="bg-cyan-600"
            testId="stat-today-attendance"
          />
          <StatCard
            icon={Notebook}
            label="Total Batches"
            value={stats.total_batches}
            color="bg-blue-500"
            testId="stat-total-batches"
          />
          <StatCard
            icon={TrendUp}
            label="Monthly Attendance"
            value={`${stats.attendance_rate}%`}
            color="bg-emerald-600"
            testId="stat-attendance-rate"
          />
          <StatCard
            icon={UserCheck}
            label="Present This Month"
            value={stats.monthly_present ?? 0}
            color="bg-teal-600"
            testId="stat-monthly-present"
          />
          <StatCard
            icon={UserMinus}
            label="Absent This Month"
            value={stats.monthly_absent ?? 0}
            color="bg-rose-600"
            testId="stat-monthly-absent"
          />
        </div>

        <p className="text-xs text-slate-500 -mt-4 mb-6">
          Attendance rate based on 8 classes/month (2 per week). Min 4 classes = 10 KWD, 5+ = 20 KWD.
        </p>

        {/* Revenue Status - Admin Only */}
        {showRevenue && revenue && (
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 mb-8" data-testid="revenue-status">
            <div className="flex items-center gap-3 mb-5">
              <CurrencyDollar size={24} weight="duotone" className="text-blue-400" />
              <h2 className="text-xl font-semibold tracking-tight text-slate-50">
                Revenue Status — {revenue.month} {revenue.year}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
              <div className="bg-slate-950 border border-slate-800 rounded-lg p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Expected</p>
                <p className="text-2xl font-black text-slate-50" data-testid="revenue-expected">KWD {revenue.total_expected}</p>
                <p className="text-xs text-slate-500 mt-1">{revenue.total_students} students (20 KWD individual / 35 KWD siblings)</p>
              </div>
              <div className="bg-slate-950 border border-emerald-800/40 rounded-lg p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-500 mb-1">Collected</p>
                <p className="text-2xl font-black text-emerald-400" data-testid="revenue-collected">KWD {revenue.total_collected}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <CheckCircle size={12} weight="bold" className="inline mr-1 text-emerald-400" />
                  {revenue.paid_students} students paid
                </p>
              </div>
              <div className="bg-slate-950 border border-amber-800/40 rounded-lg p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-500 mb-1">Outstanding</p>
                <p className="text-2xl font-black text-amber-400" data-testid="revenue-outstanding">KWD {revenue.total_outstanding}</p>
                <p className="text-xs text-slate-500 mt-1">
                  <WarningCircle size={12} weight="bold" className="inline mr-1 text-amber-400" />
                  {revenue.total_students - revenue.paid_students} students pending
                </p>
              </div>
            </div>
            {/* Progress bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-slate-400">Collection Progress</span>
                <span className="text-xs font-medium text-slate-300">
                  {revenue.total_expected > 0 ? Math.round((revenue.total_collected / revenue.total_expected) * 100) : 0}%
                </span>
              </div>
              <div className="w-full h-3 bg-slate-800 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                  style={{ width: `${revenue.total_expected > 0 ? Math.min((revenue.total_collected / revenue.total_expected) * 100, 100) : 0}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {birthdays.today_birthdays.length > 0 && (
          <div className="bg-gradient-to-r from-pink-900/30 to-purple-900/30 border border-pink-800 rounded-lg p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Cake size={32} weight="duotone" className="text-pink-400" />
              <h2 className="text-2xl font-bold tracking-tight text-slate-50">
                Birthday Today!
              </h2>
            </div>
            <div className="space-y-3">
              {birthdays.today_birthdays.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center justify-between p-4 bg-slate-900/50 border border-pink-700 rounded-md"
                  data-testid={`today-birthday-${student.id}`}
                >
                  <div>
                    <p className="font-bold text-pink-300 text-lg">{student.name}</p>
                    <p className="text-sm text-slate-400">Turning {student.age} years old</p>
                  </div>
                  <Cake size={28} weight="duotone" className="text-pink-400" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {birthdays.month_birthdays.length > 0 && (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <Cake size={24} weight="duotone" className="text-blue-400" />
                <h2 className="text-xl font-semibold tracking-tight text-slate-50">
                  {birthdays.current_month} Birthdays
                </h2>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {birthdays.month_birthdays.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center justify-between p-3 bg-slate-950 border border-slate-800 rounded-md hover:border-slate-700 transition-colors"
                    data-testid={`month-birthday-${student.id}`}
                  >
                    <div>
                      <p className="font-medium text-slate-50">{student.name}</p>
                      <p className="text-sm text-slate-400">{student.birthday_date}</p>
                    </div>
                    <span className="text-sm text-blue-400 font-medium">Age {student.age}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-50 mb-4">
              Recent Attendance
            </h2>
            
            {stats.recent_attendance.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardText size={48} weight="duotone" className="mx-auto text-slate-700 mb-4" />
                <p className="text-slate-500">No attendance records yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.recent_attendance.map((record) => (
                  <div
                    key={record.id}
                    data-testid={`recent-attendance-${record.id}`}
                    className="flex items-center justify-between p-4 bg-slate-950 border border-slate-800 rounded-md hover:border-slate-700 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-blue-600/20 flex items-center justify-center">
                        <Users size={20} weight="duotone" className="text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-50">{record.student_name}</p>
                        <p className="text-sm text-slate-500">
                          {new Date(record.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-emerald-400">
                        {record.confidence_score === 1 ? 'Manual' : `${Math.round(record.confidence_score * 100)}% match`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
